import React, { useEffect, useId, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch } from 'react-redux';

import { analyticsEvents } from '../../../services/firebase/analytics-events.service';
import { paymentSuccess } from '../../../store/packages/packagesActions';
import { rememberDeposit } from '../../../store/packages/rememberDeposit';
import API from '../../../utils/API';
import { reportWebPaymentResult } from '../../../utils/hooks/paymentStatus';
import { stripeButtonHeight } from './stripeButtonHeight';
import type { StripeLinkButtonProps } from './StripeLinkButton';

// Stripe.js must be loaded from Stripe's own host; it is not bundled.
const STRIPE_JS_URL = 'https://js.stripe.com/v3/';
/** How long Stripe gets to show its button before the app's own takes the tap. */
const LOAD_TIMEOUT_MS = 10_000;

let stripeJs: Promise<any> | null = null;
let publishableKey: Promise<string> | null = null;

const loadStripeJs = (): Promise<any> => {
	const loaded = (window as any).Stripe;
	if (loaded) return Promise.resolve(loaded);
	if (!stripeJs) {
		stripeJs = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = STRIPE_JS_URL;
			script.async = true;
			script.onload = () => resolve((window as any).Stripe);
			script.onerror = () => {
				stripeJs = null;
				reject(new Error('Stripe.js failed to load'));
			};
			document.head.appendChild(script);
		});
	}
	return stripeJs;
};

/** The casino's publishable key, once per page; a failure is asked again next time. */
const loadPublishableKey = (): Promise<string> => {
	if (!publishableKey) {
		publishableKey = API.bowaRequest(
			'/api/wallets/stripe/config',
			'get',
			undefined,
			API.getXCasinoHeader(),
		).then(response => {
			const key = response?.Data?.publishableKey;
			if (typeof key !== 'string' || !key) throw new Error('No Stripe publishable key');
			return key;
		});
		publishableKey.catch(() => {
			publishableKey = null;
		});
	}
	return publishableKey;
};

const currencyOf = (pkg: StripeLinkButtonProps['pkg']): string =>
	String((pkg as any)?.currency?.name || (pkg as any)?.currency || 'EUR');

/**
 * Stripe's own Link button (Express Checkout Element, Link only). Stripe opens Link's window only
 * from a click on its own button, so this button, not the app's, is what the player taps: Link
 * opens at once, and once the player approves there the deposit is opened (a PaymentIntent, the
 * `intent` deposit) and confirmed in this page. Nothing is created until then, so closing Link
 * costs nothing. The result reaches the app-level PaymentStatusModal like a checkout tab's.
 * Until Stripe's button is ready, and wherever Link is not offered, the app's own button stays.
 */
const StripeLinkButton = ({
	pkg,
	recipient,
	frontendUrl,
	height,
	fallback,
	placeholder,
	onLoadingChange,
}: StripeLinkButtonProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const mountId = `stripe-link-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
	const [phase, setPhase] = useState<'loading' | 'ready' | 'unavailable'>('loading');
	const [error, setError] = useState<string | null>(null);

	const amount = Math.round(Number(pkg.price || 0) * 100);
	const currency = currencyOf(pkg);
	const buttonHeight = stripeButtonHeight(height);

	useEffect(() => {
		let cancelled = false;
		let element: any = null;
		setPhase('loading');
		setError(null);
		// Stripe never answering leaves the app's own button to pay with, rather than a dead slot.
		const giveUp = setTimeout(() => {
			if (cancelled) return;
			console.warn('[StripeLinkButton] Stripe did not load the button in time');
			setPhase(current => (current === 'loading' ? 'unavailable' : current));
		}, LOAD_TIMEOUT_MS);

		const failedMessage = () =>
			t('T_PAYMENT_FAILED', { defaultValue: 'Payment failed. Please try again.' });

		/** Opens the deposit for what the player approved in Link: a PaymentIntent to confirm. */
		const openDeposit = async () => {
			const response = await API.bowaRequest(
				'/api/wallets/deposit/stripe',
				'post',
				{
					amount: Number(pkg.price || 0),
					currency,
					frontendUrl,
					packageId: pkg.id,
					paymentType: 'link',
					intent: true,
					...(recipient ? { recipientPlayerId: recipient.playerId } : {}),
				},
				{ 'Content-Type': 'application/json', ...API.getXCasinoHeader() },
			);
			const data = response?.Data;
			if (response?.Error || data?.error || data?.status !== 'success') {
				throw new Error(data?.error || 'Deposit refused');
			}
			const { clientSecret, depositId, package: packageData } = data.data ?? {};
			if (!clientSecret || !depositId) throw new Error('No payment to confirm');
			return { clientSecret: String(clientSecret), depositId: String(depositId), packageData };
		};

		Promise.all([loadStripeJs(), loadPublishableKey()])
			.then(([Stripe, key]) => {
				if (cancelled) return;
				const stripe = Stripe(key);
				const elements = stripe.elements({
					mode: 'payment',
					amount,
					currency: currency.toLowerCase(),
					// Stripe takes Link only with card beside it; card is no button here, so only
					// Link shows. The deposit's PaymentIntent names the same two.
					paymentMethodTypes: ['link', 'card'],
					// The corners of the sheet's wallet buttons (6 on 48), so it matches PayPal's above.
					appearance: { variables: { borderRadius: `${Math.round(buttonHeight / 8)}px` } },
				});
				element = elements.create('expressCheckout', {
					paymentMethods: {
						link: 'auto',
						applePay: 'never',
						googlePay: 'never',
						paypal: 'never',
						amazonPay: 'never',
						klarna: 'never',
					},
					// No `layout`: Link is the only button, and Stripe refuses some combinations
					// (`overflow: 'never'` with a row limit) by never finishing the load.
					buttonHeight,
				});

				// No Link here (not enabled for the account, the domain not registered with Stripe,
				// or a browser Stripe does not offer it in): the app's own button stays.
				element.on('ready', ({ availablePaymentMethods }: any) => {
					console.info('[StripeLinkButton] Stripe offers:', availablePaymentMethods ?? 'nothing');
					if (!cancelled) setPhase(availablePaymentMethods?.link ? 'ready' : 'unavailable');
				});
				element.on('loaderror', ({ error: loadError }: any) => {
					console.error('[StripeLinkButton] Stripe could not load the button:', loadError);
					if (!cancelled) setPhase('unavailable');
				});
				element.on('click', (event: any) => {
					setError(null);
					event.resolve();
				});
				element.on('confirm', async (event: any) => {
					try {
						const { error: submitError } = await elements.submit();
						if (submitError) throw submitError;

						analyticsEvents.trackBeginCheckout(pkg.id);
						const deposit = await openDeposit();
						rememberDeposit({
							depositId: deposit.depositId,
							packageData: deposit.packageData,
							pkg,
							recipient,
						});

						const { error: confirmError } = await stripe.confirmPayment({
							elements,
							clientSecret: deposit.clientSecret,
							confirmParams: {
								return_url: `${frontendUrl}/?payment=success&deposit_id=${deposit.depositId}`,
							},
							redirect: 'if_required',
						});
						if (confirmError) throw confirmError;

						// Closes the shop; the webhook credits the package, the status popup says so.
						dispatch(
							paymentSuccess({
								provider: 'link',
								depositId: deposit.depositId,
								package: deposit.packageData,
							}),
						);
						reportWebPaymentResult({ status: 'success', depositId: deposit.depositId });
					} catch (failure: any) {
						console.error('[StripeLinkButton] payment failed:', failure);
						event.paymentFailed?.({ reason: 'fail' });
						if (!cancelled) setError(failure?.message || failedMessage());
					}
				});

				element.mount(`#${mountId}`);
			})
			.catch(failure => {
				console.error('[StripeLinkButton] Link unavailable:', failure);
				if (!cancelled) setPhase('unavailable');
			});

		return () => {
			cancelled = true;
			clearTimeout(giveUp);
			element?.destroy?.();
		};
		// The button is rebuilt for another package or amount, not for every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pkg.id, amount, currency, recipient?.playerId, frontendUrl, buttonHeight]);

	// Before paint, so the host never shows its buttons for a frame before they start waiting.
	useLayoutEffect(() => {
		onLoadingChange?.(phase === 'loading');
	}, [phase, onLoadingChange]);
	useLayoutEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

	const ready = phase === 'ready';

	return (
		<View style={[styles.slot, { minHeight: height }]}>
			{ready ? null : phase === 'loading' && placeholder ? (
				<View style={{ height }}>{placeholder}</View>
			) : phase === 'loading' ? (
				// Shown dimmed and inactive while Stripe's loads: a tap here would take the
				// checkout-page route instead of opening Link.
				<View style={[styles.waiting, { height }]} pointerEvents='none'>
					{fallback}
				</View>
			) : (
				fallback
			)}
			{phase === 'unavailable' ? null : (
				// Stripe renders its button in here; kept invisible until it is ready to take a tap.
				<View
					nativeID={mountId}
					style={[styles.mount, ready ? null : styles.hidden]}
					pointerEvents={ready ? 'auto' : 'none'}
				/>
			)}
			{error ? (
				<Text style={styles.error} numberOfLines={2}>
					{error}
				</Text>
			) : null}
		</View>
	);
};

const styles = StyleSheet.create({
	slot: { alignSelf: 'stretch', justifyContent: 'center' },
	mount: { width: '100%' },
	hidden: { position: 'absolute', left: 0, right: 0, opacity: 0 },
	waiting: { opacity: 0.55 },
	error: {
		marginTop: 4,
		fontFamily: 'Sofia Sans',
		fontSize: 11,
		lineHeight: 13,
		fontWeight: '600',
		color: '#FF9B8A',
		textAlign: 'center',
	},
});

export default StripeLinkButton;
